import React from 'react';
import { Trash2, Reply } from 'lucide-react';
import { Card } from '../../atoms/Card';
import { Button } from '../../atoms/Button';
import { type Comment } from '../../../services/commentsService';
import { getUserProfileUrl } from '../../../utils/urlUtils';

const WEB_BASE_URL = process.env.REACT_APP_WEB_URL || 'https://sosyal.media';

const getNewsUrl = (newsSlug: string): string => {
  const base = WEB_BASE_URL.endsWith('/') ? WEB_BASE_URL.slice(0, -1) : WEB_BASE_URL;
  return `${base}/i/${newsSlug}?ig=true`;
};

export interface CommentCardProps {
  comment: Comment;
  deletingId: number | null;
  onDeleteClick: (comment: Comment) => void;
}

export const CommentCard = ({ comment, deletingId, onDeleteClick }: CommentCardProps) => {
  const isDeleting = deletingId === comment.id;

  const handleAuthorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (comment.user.username) {
      window.open(getUserProfileUrl(comment.user.username), '_blank');
    }
  };

  const handleNewsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log(comment.news);
    window.open(getNewsUrl(comment.news.slug), '_blank');
  };

  return (
    <Card className={comment.isDelete ? 'opacity-60' : ''}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-gray-800 break-words whitespace-pre-wrap leading-relaxed">
              {comment.content}
            </p>
          </div>
          {/* Desktop delete button */}
          <div className="hidden sm:flex flex-shrink-0 ml-4">
            <Button
              variant="danger"
              size="small"
              onClick={(e) => { e.stopPropagation(); onDeleteClick(comment); }}
              disabled={isDeleting || comment.isDelete}
              isLoading={isDeleting}
              className="flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Sil
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500">
            <span className="whitespace-nowrap">ID: {comment.id}</span>
            <button
              onClick={handleAuthorClick}
              className="whitespace-nowrap text-accent hover:underline"
            >
              {comment.user.username ?? `Kullanıcı #${comment.user.id}`}
            </button>
            <button
              onClick={handleNewsClick}
              className="whitespace-nowrap text-accent hover:underline max-w-[200px] truncate"
              title={comment.news.title ?? `İçerik #${comment.news.id}`}
            >
              {comment.news.title ?? `İçerik #${comment.news.id}`}
            </button>
            <span className="whitespace-nowrap">
              {new Date(comment.createdAt).toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            {comment.parentCommentId && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded whitespace-nowrap">
                <Reply className="w-3 h-3" />
                Yanıt
              </span>
            )}
            {!comment.isActive && (
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded whitespace-nowrap">
                Pasif
              </span>
            )}
            {comment.isDelete && (
              <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded whitespace-nowrap">
                Silinmiş
              </span>
            )}
          </div>
          {/* Mobile delete button */}
          <div className="flex sm:hidden">
            <Button
              variant="danger"
              size="small"
              onClick={(e) => { e.stopPropagation(); onDeleteClick(comment); }}
              disabled={isDeleting || comment.isDelete}
              isLoading={isDeleting}
              className="flex items-center justify-center gap-2 w-full"
            >
              <Trash2 className="w-4 h-4" />
              Sil
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
